import { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import DealDetailModal from "../../components/DealDetailModal";
import DealSubmissionModal from "../../components/DealSubmissionModal";
import DocumentChecklistModal from "../../components/DocumentChecklistModal";
import InvestorInterestModal from "../../components/InvestorInterestModal";
import {
  Briefcase,
  MapPin,
  DollarSign,
  Clock,
  CheckCircle,
  FileText,
  Eye,
  Pencil,
  Heart,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  pending: "Pending Review",
  reserved: "Reserved",
  in_closing: "In Closing",
  closed: "Closed",
  interested: "Interested",
};

const STATUS_COLORS = {
  pending: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  reserved: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  in_closing: "bg-purple-500/10 text-purple-500 border-purple-500/20",
  closed: "bg-green-500/10 text-green-500 border-green-500/20",
  interested: "bg-pink-500/10 text-pink-500 border-pink-500/20",
};

const DEAL_TYPE_LABELS = {
  flip: "Flip",
  rental: "Rental",
  development: "Development",
};

const DEAL_TYPE_COLORS = {
  flip: "bg-orange-500/10 text-orange-500",
  rental: "bg-blue-500/10 text-blue-500",
  development: "bg-purple-500/10 text-purple-500",
};

const InvestorMyDealsPage = () => {
  const { user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [interestDeal, setInterestDeal] = useState(null);
  const [checklistKey, setChecklistKey] = useState(0);
  const [activeTab, setActiveTab] = useState("all");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchMyDeals = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/investor/my-deals`, {
        withCredentials: true,
        headers,
      });
      setDeals(response.data);
    } catch (error) {
      console.error("Failed to fetch deals:", error);
      toast.error("Failed to fetch your deals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyDeals();
  }, []);

  const filteredDeals = deals.filter((deal) => {
    if (activeTab === "all") return true;
    if (activeTab === "pending") return deal.status === "pending";
    if (activeTab === "reserved") return deal.status === "reserved";
    if (activeTab === "in_closing") return deal.status === "in_closing";
    if (activeTab === "closed") return deal.status === "closed";
      if (activeTab === "interested") return deal.is_interested;

    return true;
  });

  const formatCurrency = (value) => {
    if (!value) return "$0";
    return "$" + value.toLocaleString();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getSignedDocCount = (deal) => {
    if (!deal.documents_signed) return 0;
    return Object.values(deal.documents_signed).filter((v) => v !== null)
      .length;
  };

  const handleViewDeal = (deal) => {
    setSelectedDeal(deal);
    setShowDetailModal(true);
  };

  const handleEditDeal = (deal) => {
    setEditingDeal(deal);
    setShowEditModal(true);
  };

  const handleExpressInterest = (deal) => {
    setInterestDeal(deal);
    setShowInterestModal(true);
  };

  const handleViewChecklist = (deal) => {
    setSelectedDeal(deal);
    setChecklistKey((k) => k + 1);
    setShowChecklistModal(true);
  };

  const handleDealUpdated = () => {
    fetchMyDeals();
  };

  const handleInterestChange = () => {
    fetchMyDeals();
  };

  const pendingCount = deals.filter((d) => d.status === "pending").length;
  const reservedCount = deals.filter((d) => d.status === "reserved").length;
  const inClosingCount = deals.filter((d) => d.status === "in_closing").length;
  const closedCount = deals.filter((d) => d.status === "closed").length;
  const interestedCount = deals.filter((d) => d.is_interested).length;


  return (
    <DashboardLayout title="My Deals">
      <div
        className="space-y-6 animate-fade-in"
        data-testid="investor-my-deals-page"
      >
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="stat-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Total Deals
              </CardTitle>
              <Briefcase className="w-4 h-4 text-blue-600" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold">{deals.length}</div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Pending Review
              </CardTitle>
              <Clock className="w-4 h-4 text-orange-500" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold text-orange-500">
                {pendingCount}
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Reserved
              </CardTitle>
              <Clock className="w-4 h-4 text-blue-500" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold text-blue-500">
                {reservedCount}
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">
                Closed
              </CardTitle>
              <CheckCircle className="w-4 h-4 text-green-500" />
            </CardHeader>
            <CardContent className="pt-0">
              <div className="text-2xl font-bold text-green-500">
                {closedCount}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Deals List */}
        <Card>
          <CardHeader>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  My Deals
                </CardTitle>
                <TabsList>
                  <TabsTrigger value="all">All ({deals.length})</TabsTrigger>
                  <TabsTrigger value="pending">
                    Pending ({pendingCount})
                  </TabsTrigger>
                  <TabsTrigger value="interested">
                    Interested ({interestedCount})
                  </TabsTrigger>
                   <TabsTrigger value="reserved">
                    Reserved ({reservedCount})
                  </TabsTrigger>
                  <TabsTrigger value="in_closing">
                    In Closing ({inClosingCount})
                  </TabsTrigger>
                  <TabsTrigger value="closed">
                    Closed ({closedCount})
                  </TabsTrigger>
                </TabsList>
              </div>
            </Tabs>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="loading-spinner border-blue-600"></div>
              </div>
            ) : filteredDeals.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No deals found</p>
                <p className="text-sm mt-1">
                  {activeTab === "all"
                    ? "You haven't reserved any deals yet"
                    : `No ${activeTab.replace("_", " ")} deals`}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredDeals.map((deal) => (
                  <Card
                    key={deal.deal_id}
                    className={`hover:border-primary/50 transition-colors ${
                      deal.assigned_by_admin
                        ? "border-yellow-500/50 bg-yellow-500/5"
                        : ""
                    }`}
                  >
                    <CardContent className="pt-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">
                              {deal.property_address}
                            </h3>
                            <Badge
                              variant="outline"
                              className={STATUS_COLORS[deal.status]}
                            >
                              {STATUS_LABELS[deal.status]}
                            </Badge>
                            <Badge className={DEAL_TYPE_COLORS[deal.deal_type]}>
                              {DEAL_TYPE_LABELS[deal.deal_type]}
                            </Badge>
                            {deal.assigned_by_admin && (
                              <Badge className="bg-yellow-500/10 text-yellow-600">
                                Assigned by Admin
                              </Badge>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {deal.city}, {deal.state}
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              {formatCurrency(deal.asking_price)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              Reserved: {formatDate(deal.reserved_at)}
                            </span>
                          </div>

                          {/* Document Progress */}
                          {deal.status !== "closed" &&
                            deal.status !== "pending" && (
                              <div className="mt-3">
                                <div className="flex items-center justify-between text-sm mb-1">
                                  <span className="text-muted-foreground">
                                    Documents Signed
                                  </span>
                                  <span className="font-medium">
                                    {getSignedDocCount(deal)}/6
                                  </span>
                                </div>
                                <div className="h-2 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-blue-500 transition-all"
                                    style={{
                                      width: `${(getSignedDocCount(deal) / 6) * 100}%`,
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                        </div>

            <div className="flex items-center gap-2">
            {deal.status === "pending" && deal.submitted_by === user?.user_id ? (
              <Button variant="outline" size="sm" onClick={() => handleEditDeal(deal)}>
                <Pencil className="w-4 h-4 mr-1" />
                Edit Deal
              </Button>
            ) : (deal.status === "pending" ||
                deal.status === "approved" ||
                deal.status === "active") &&
              !deal.reserved_by ? (
              deal.is_interested ? (
                <Button variant="outline" size="sm" onClick={() => handleExpressInterest(deal)}>
                  <Heart className="w-4 h-4 mr-1 fill-current" />
                  Interest Expressed
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => handleExpressInterest(deal)}>
                  <Heart className="w-4 h-4 mr-1" />
                  Express Interest
                </Button>
              )
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => handleViewDeal(deal)}>
                  <Eye className="w-4 h-4 mr-1" />
                  View Deal
                </Button>
                {deal.status !== "closed" && (
                  <Button size="sm" onClick={() => handleViewChecklist(deal)}>
                    <FileText className="w-4 h-4 mr-1" />
                    Documents
                  </Button>
                )}
              </>
            )}
          </div>

                              </div>
                            </CardContent>
                          </Card>
                        ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Deal Detail Modal */}
      {selectedDeal && (
        <DealDetailModal
          deal={selectedDeal}
          open={showDetailModal}
          onOpenChange={setShowDetailModal}
          onDealUpdated={handleDealUpdated}
        />
      )}

      {/* Document Checklist Modal */}
      {selectedDeal && (
        <DocumentChecklistModal
          key={checklistKey}
          deal={selectedDeal}
          open={showChecklistModal}
          onOpenChange={setShowChecklistModal}
          onDocumentSigned={handleDealUpdated}
        />
      )}

      {/* Deal Edit Modal */}
      {editingDeal && (
        <DealSubmissionModal
          open={showEditModal}
          onOpenChange={(open) => {
            setShowEditModal(open);
            if (!open) setEditingDeal(null);
          }}
          onSuccess={handleDealUpdated}
          draftData={editingDeal}
        />
      )}

      {/* Express Interest Modal */}
      {interestDeal && (
        <InvestorInterestModal
          deal={interestDeal}
          open={showInterestModal}
          onOpenChange={(open) => {
            setShowInterestModal(open);
            if (!open) setInterestDeal(null);
          }}
          onInterestChange={handleInterestChange}
        />
      )}
    </DashboardLayout>
  );
};

export default InvestorMyDealsPage;